import { isAuthed } from './auth';
import { renderLogin } from './views/login';
import { renderOverview } from './views/overview';
import { renderOrgsList } from './views/orgs-list';
import { renderOrgDetail } from './views/org-detail';
import { renderAppAnalytics } from './views/app-analytics';
import { renderMarket } from './views/market';
import { renderDecisions } from './views/decisions';
import { renderSecurity } from './views/security';
import { renderRemoteConfig } from './views/remote-config';
import { renderSubscriptionsAdmin } from './views/subscriptions';
import { renderBarcodes } from './views/barcodes';
import { renderDocumentProfiles } from './views/document-profiles';
import { renderAccounts } from './views/accounts';
import { renderEmails } from './views/emails';
import { renderWaitlist } from './views/waitlist';
import { renderVenueRequests } from './views/venue-requests';
import { renderPassportCertificates } from './views/passport-certificates';

export type RouteContext = {
  root: HTMLElement;
  params: Record<string, string>;
  query: URLSearchParams;
};

type Route = {
  pattern: RegExp;
  protected: boolean;
  render: (ctx: RouteContext) => void | Promise<void>;
};

const routes: Route[] = [
  { pattern: /^\/login\/?$/, protected: false, render: renderLogin },
  { pattern: /^\/security\/?$/, protected: true, render: renderSecurity },
  { pattern: /^\/config\/?$/, protected: true, render: renderRemoteConfig },
  {
    pattern: /^\/subscriptions\/?$/,
    protected: true,
    render: renderSubscriptionsAdmin,
  },
  { pattern: /^\/barcodes\/?$/, protected: true, render: renderBarcodes },
  {
    pattern: /^\/document-profiles\/?$/,
    protected: true,
    render: renderDocumentProfiles,
  },
  { pattern: /^\/accounts\/?$/, protected: true, render: renderAccounts },
  { pattern: /^\/emails\/?$/, protected: true, render: renderEmails },
  { pattern: /^\/waitlist\/?$/, protected: true, render: renderWaitlist },
  {
    pattern: /^\/venue-requests\/?$/,
    protected: true,
    render: renderVenueRequests,
  },
  {
    pattern: /^\/passport-certificates\/?$/,
    protected: true,
    render: renderPassportCertificates,
  },
  { pattern: /^\/app\/?$/, protected: true, render: renderAppAnalytics },
  { pattern: /^\/market\/?$/, protected: true, render: renderMarket },
  { pattern: /^\/decisions\/?$/, protected: true, render: renderDecisions },
  {
    pattern: /^\/organizations\/([^/]+)\/?$/,
    protected: true,
    render: renderOrgDetail,
  },
  {
    pattern: /^\/(?:teams|organizations)\/?$/,
    protected: true,
    render: renderOrgsList,
  },
  { pattern: /^\/$/, protected: true, render: renderOverview },
];

let appRoot: HTMLElement;

/** Programmatic navigation — pushes history then re-renders. */
export function navigate(path: string): void {
  if (path !== location.pathname + location.search) {
    history.pushState({}, '', path);
  }
  void resolve();
}

async function resolve(): Promise<void> {
  const path = location.pathname;
  const query = new URLSearchParams(location.search);

  for (const route of routes) {
    const match = route.pattern.exec(path);
    if (!match) continue;

    if (route.protected && !isAuthed()) {
      history.replaceState({}, '', '/login');
      void renderLogin({ root: appRoot, params: {}, query });
      return;
    }

    const params: Record<string, string> = {};
    if (match[1]) params.orgId = decodeURIComponent(match[1]);
    appRoot.removeAttribute('aria-busy');
    await route.render({ root: appRoot, params, query });
    return;
  }

  // Unknown path → home.
  navigate('/');
}

export function startRouter(root: HTMLElement): void {
  appRoot = root;
  window.addEventListener('popstate', () => void resolve());
  void resolve();
}
